import { inject, Injectable } from '@angular/core';
import { AgentCategory } from '../models/agent-config.model';
import { AiClientService } from '../services/ai-client.service';
import { AiConversation, TokenUsage } from '../transports/ai-transport';
import { CardResponse, CardResponseSchema, parseCardResponse } from './card.schema';
import { RecordKind, RecordRef } from './record-ref';

/** Agent category for the Nova Insights surface (insight cards) of a kind. */
export function categoryFor(kind: RecordKind): AgentCategory {
  switch (kind) {
    case 'contact': return AgentCategory.ContactProfileSummary;
    case 'account': return AgentCategory.AccountProfileSummary;
    case 'spotlight': return AgentCategory.ContactSpotlightSummary;
  }
}

/** The card plus the turn's feedback-correlation metadata (response id + token usage). */
export interface InsightsResult {
  card: CardResponse;
  responseId?: string;
  usage?: TokenUsage;
}

/**
 * Nova Insights surface: a unified `CardResponse` (insight sections + optional charts/metrics)
 * from the per-kind profile/spotlight agents (ContactProfile/AccountProfile/ContactSpotlight).
 * Holds its own conversation so it never clobbers the AI Summary card's thread for the same
 * record.
 *
 * Lifecycle:
 *  - `startConversation(record)` resets the handle on screen launch (no cached restore —
 *    insights always generate fresh against the current SummaryConfig).
 *  - `analyze(record)` discards the prior conversation, creates a fresh one, and triggers the
 *    agent; the transport assembles + attaches grounding from the agent config + recordId.
 *  - `endConversation()` releases the local handle (no server-side delete).
 */
@Injectable({ providedIn: 'root' })
export class RecordInsightsFeature {
  #analysisConversation: AiConversation | null = null;
  readonly #ai = inject(AiClientService);

  /** Whether an insights (Nova) agent is configured for this kind. */
  isAvailable(kind: RecordKind): boolean {
    return this.#ai.isAvailable(categoryFor(kind));
  }

  /**
   * Screen launch. Insights always generate fresh against the current SummaryConfig — there is no
   * cached restore. Returns null so the surface shows the "Generate" empty state until the rep
   * triggers analyze().
   */
  async startConversation(_record: RecordRef): Promise<CardResponse | null> {
    this.#analysisConversation = null;
    return null;
  }

  /**
   * Discard the prior insights conversation, create a fresh one for the given record's
   * category, trigger the agent, and return its `CardResponse`. The record is passed explicitly
   * (not read from sticky state) so interleaved loads can't cross-wire the surface.
   *
   * Grounding is assembled by the transport from the agent config + this recordId; the user
   * turn is only the trigger phrase.
   */
  async analyze(record: RecordRef): Promise<InsightsResult> {
    const previous = this.#analysisConversation;

    const conv = await this.#ai.startConversation(categoryFor(record.kind));

    // The transport assembles + attaches grounding (and the rep's session/identity context);
    // recordId scopes it, recordType/origin are this surface's own call metadata.
    conv.setStructuredInputs({
      recordId: record.id ?? '',
      recordType: record.kind,
      origin: 'goapp-insights',
    });

    const input = [{ type: 'message', role: 'user', content: 'Provide the insights.' }];

    // The insights agent emits a `card_response` envelope. parse() unfences + JSON-parses but
    // does NOT validate; parseCardResponse runs the zod schema and recovers gracefully when a
    // weaker model slips — malformed sections are dropped, never the whole card.
    const raw = await conv.parse<unknown>(input, CardResponseSchema, 'card_response');
    const card = parseCardResponse(raw);

    if (!card.sections.length) {
      throw new Error('Agent returned no card sections for analyze');
    }

    this.#analysisConversation = conv;

    if (previous && previous.id !== conv.id) {
      await previous.delete().catch(() => undefined); // discard the superseded analysis
    }
    const lastTurn = conv.lastTurn;
    return { card, responseId: lastTurn?.responseId, usage: lastTurn?.usage };
  }

  /** Releases the local handle without deleting server-side. */
  async endConversation(): Promise<void> {
    this.#analysisConversation = null;
  }
}
